import { useEffect, useState } from "react";
import { Box, Button, Heading, Text, Flex, Link } from "rebass";
import { useTheme, ThemeProvider, withTheme } from "@emotion/react";
import theme from "../styles/LLTheme";
import ReactMarkdown from "react-markdown";
import Head from "next/head";
import Image from "next/image";
import { Tiles } from "@rebass/layout";

const DownloadCard = ({
  title,
  body,
  size,
  isDeplay,
}: {
  title: string;
  body: string;
  size: number;
  isDeplay: boolean;
}) => {
  return (
    <Flex
      sx={{
        padding: "0 0 0",
        borderTop: "solid 2px rgba(255, 255, 255, 0.24)",
        alignItems: "flex-start",
        maxWidth: "500px",
        justifyContent: "space-between",
        [theme.breakpoints.down("md")]: {
          padding: "40px 0",
        },
      }}
      flexDirection="column"
      ml={["3%", 0]}
      pb={[0, "10%"]}
    >
      <Text
        as="h1"
        fontSize={["1.5em", "1.8em", "2em"]}
        sx={{ color: "#FFFFFF", fontFamily: "Inter,sans-serif", py: "12px" }}
      >
        {title}
      </Text>
      <Text
        fontSize={["1em", "1.2em", "1.3em"]}
        sx={{ color: "#E0E0E0", fontFamily: "Inter,sans-serif" }}
      >
        {body}
      </Text>
    </Flex>
  );
};

const Browser = (icon: string, name: string, link: string | null) => {
  return (
    <Button
      onClick={() => {
        link && window.open(link)?.focus();
      }}
      sx={{
        backgroundColor: "transparent",
        cursor: "pointer",
        // border: "1px solid #343434",
        // borderRadius: "20px",
        transition: "all 0.3s, opacity 0.3s",
        ":hover": {
          // backgroundColor: "#ffffff",
          transform: "scale(1.1)"
        },
      }}
      pb={[0, "20%"]}
    >
      <Flex
        sx={{ justifyContent: "center", alignItems: "center", gap: "8px" }}
        flexDirection="column"
      >
        <Box width={[40, 50, 60]} height={[40, 50, 60]}>
          <Image
            src={icon}
            alt="secure-box"
            width={"100%"}
            height={"100%"}
            style={{
              maxWidth: "100%",
              height: "auto"
            }} />
        </Box>
        <Text sx={{ padding: "11px" }}>{name}</Text>
      </Flex>
    </Button>
  );
};
const TargetCard = ({
  title,
  body,
  size,
  isDeplay,
}: {
  title: string;
  body: string;
  size: number;
  isDeplay: boolean;
}) => {
  return (
    <Flex
      sx={{
        padding: "32px 0 70px",
        borderTop: "solid 2px rgba(255, 255, 255, 0.24)",
        alignItems: "flex-start",
        maxWidth: "500px",
        [theme.breakpoints.down("md")]: {
          padding: "40px 0",
        },
      }}
      flexDirection="column"
    >
      <Text
        as="h1"
        fontSize={["1.5em", "1.8em", "2em"]}
        sx={{ color: "#f9f9f9", fontFamily: "Inter,sans-serif", py: "12px" }}
      >
        {title}
      </Text>
      <Text
        fontSize={["1em", "1.2em", "1.3em"]}
        sx={{ color: "#999999", fontFamily: "Inter,sans-serif" }}
      >
        {body}
      </Text>
    </Flex>
  );
};
const Download = () => {
  return (
    <ThemeProvider theme={theme}>
      <Flex flexDirection="column" m="100px auto" width="80%" sx={{gap: "5vh"}} >
        <Flex
          backgroundColor="#262626"
          sx={{
            borderRadius: "24px",
          }}
        >
          <Tiles
            columns={[1, 2]}
            sx={{
              backgroundImage: "url('/download1.svg')",
              backgroundSize: "cover",
              // outflow: "hidden",
              borderRadius: "24px",
              width: "100%",
            }}
            p="4%"
          >
            <Flex width="100%" sx={{display: "relative"}}>
              <Image
                alt="mobile-image"
                src="/mobile.svg"
                width="500%"
                height="350%"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  objectPosition: "center"
                }} />
            </Flex>

            <Flex flexDirection="column" sx={{justifyContent: "space-between"}}>
              <DownloadCard
                title="Lilico for Mobile"
                body="Access and Manage your favourite dApp with in your mobile device"
                size={80}
                isDeplay={true}
              />
              <Tiles
                columns={[1, 2]}
                sx={{
                  gap: "10px",
                }}
                mb={["27%", "8%"]}
                mx={["10%", 0]}
              >
                <Link href="https://play.google.com/store/apps/details?id=io.outblock.lilico">
                  <Image
                    src="/images/GooglePlay.svg"
                    alt="googledownload"
                    width="270"
                    height="80"
                    style={{
                      maxWidth: "100%",
                      height: "auto",
                      objectPosition: "center"
                    }} />
                </Link>
                <Link href="https://apps.apple.com/us/app/lilico/id1644169603">
                  <div style={{ position: "relative", textAlign: "inherit" }}>
                    <Image
                      src="/images/AppStore.svg"
                      alt="appledownload"
                      width="270"
                      height="80"
                      style={{
                        maxWidth: "100%",
                        height: "auto",
                        objectPosition: "center"
                      }} />
                  </div>
                </Link>
              </Tiles>
            </Flex>
          </Tiles>
        </Flex>
        <Flex
          backgroundColor="#262626"
          sx={{
            borderRadius: "24px",
          }}
        >
          <Tiles
            columns={[1, 2]}
            sx={{
              backgroundImage: "url('/download2.svg')",
              backgroundSize: "cover",
              borderRadius: "24px",
              width: "100%",
            }}
            px="4%"
            pt="4%"
          >
            <Flex>
              <Flex flexDirection="column">
                <DownloadCard
                  title="Lilico for Desktop"
                  body="Live in your browser, Lilico enables you to access Web 3.0, NFTs, tokens and dApps easily and simple."
                  size={80}
                  isDeplay={true}
                />
                <Tiles columns={[3, 2, 3]} sx={{ gap: "0%" }} mt={[0, "6%"]}>
                  {Browser(
                    "/images/chrome.png",
                    "Chrome",
                    "https://chrome.google.com/webstore/detail/lilico/hpclkefagolihohboafpheddmmgdffjm"
                  )}
                  {Browser(
                    "/images/brave.png",
                    "Brave",
                    "https://chrome.google.com/webstore/detail/lilico/hpclkefagolihohboafpheddmmgdffjm"
                  )}
                  {Browser(
                    "/images/edge.png",
                    "Edge",
                    "https://chrome.google.com/webstore/detail/lilico/hpclkefagolihohboafpheddmmgdffjm"
                  )}
                </Tiles>
              </Flex>
            </Flex>
            <Flex width="100%" sx={{overflow: "visible"}}>
              <Image
                alt="app"
                src="/app.svg"
                width="500%"
                height="350%"
                style={{
                  transform: "scale(1.0)",
                  overflow: "visible",
                  maxWidth: "100%",
                  height: "auto",
                  objectPosition: "center"
                }} />
            </Flex>
          </Tiles>
        </Flex>
      </Flex>
    </ThemeProvider>
  );
};

export default Download;
